import { apiFetch } from '@sdc/auth';
import type { PollInput, PollView, ResultsVisibility } from '../../shared/polls';
import type { Role } from '../../shared/validation';

/** Admin-Endpoints (Netlify Functions mit Service-Role-Key). */

export interface AdminApp {
  id: string;
  slug: string;
  name: string;
  url: string;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  /**
   * Waehlbare Rollen dieser App, aus `apps.roles`. Der MPS Manager
   * unterscheidet sechs, andere Apps nur `user` und `admin`. Faellt das Feld
   * weg (aeltere Antwort), gilt der Rueckfall im Panel.
   */
  roles?: string[];
}

export interface AdminUser {
  id: string;
  email: string | null;
  display_name: string | null;
  /** erecno aus Zoho People, sofern das Konto von dort uebernommen wurde. */
  zoho_user_id: string | null;
  is_active: boolean;
  has_profile: boolean;
  last_sign_in_at: string | null;
  invited_at: string | null;
  confirmed: boolean;
  permissions: { app_id: string; role: string }[];
}

export interface AdminAnnouncement {
  id: string;
  title: string;
  body_md: string;
  valid_until: string | null;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminPoll {
  id: string;
  title: string;
  body_md: string;
  multiple: boolean;
  anonymous: boolean;
  results_visibility: ResultsVisibility;
  closes_at: string;
  created_by: string | null;
  created_at: string;
  open: boolean;
  mine: boolean;
  options: { id: string; label: string }[];
  participants: number;
  results: { option_id: string; count: number }[] | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    const message = typeof payload.error === 'string' ? payload.error : `Fehler ${res.status}`;
    throw new Error(message);
  }
  return payload as T;
}

function postJson(path: string, body: unknown, method = 'POST'): RequestInit & { path: string } {
  return {
    path,
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export const adminApi = {
  listUsers: () => call<{ users: AdminUser[]; apps: AdminApp[] }>('/api/admin-users'),

  invite: (email: string, displayName: string) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'invite',
      email,
      display_name: displayName,
    });
    return call<{
      user_id: string;
      email: string;
      versendet: boolean;
      link?: string;
      hinweis?: string;
    }>(path, init);
  },

  /**
   * Konto mit Zugangsdaten anlegen. Das Passwort kommt aus der Function und
   * steht genau in dieser einen Antwort — es laesst sich spaeter nirgends
   * nachschlagen, nur neu setzen.
   */
  createUser: (email: string, displayName: string, passwort?: string, zohoUserId?: string) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'create_user',
      email,
      display_name: displayName,
      ...(passwort ? { passwort } : {}),
      ...(zohoUserId ? { zoho_user_id: zohoUserId } : {}),
    });
    return call<{ user_id: string; email: string; passwort: string }>(path, init);
  },

  /** Neues Einmalpasswort fuer ein bestehendes Konto. */
  resetPassword: (userId: string, passwort?: string) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'reset_password',
      user_id: userId,
      ...(passwort ? { passwort } : {}),
    });
    return call<{ user_id: string; email: string | null; passwort: string }>(path, init);
  },

  /**
   * Anmeldung freigeben, ohne das Passwort anzufassen. Ohne bestaetigte
   * Adresse laesst Supabase niemanden herein, auch mit richtigem Passwort.
   */
  confirmUser: (userId: string) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'confirm_user',
      user_id: userId,
    });
    return call<{ user_id: string; email: string | null; confirmed: boolean }>(path, init);
  },

  /** Konto endgueltig loeschen — nicht umkehrbar. */
  deleteUser: (userId: string) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'delete_user',
      user_id: userId,
    });
    return call<{ user_id: string; geloescht: boolean }>(path, init);
  },

  setActive: (userId: string, isActive: boolean) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'set_active',
      user_id: userId,
      is_active: isActive,
    });
    return call<{ user_id: string; is_active: boolean }>(path, init);
  },

  resendInvite: (userId: string, nurLink = false) => {
    const { path, ...init } = postJson('/api/admin-users', {
      action: 'resend_invite',
      user_id: userId,
      nur_link: nurLink,
    });
    return call<{
      user_id: string;
      email: string;
      versendet: boolean;
      link?: string | null;
      hinweis?: string;
    }>(path, init);
  },

  setPermission: (userId: string, appId: string, role: Role | null) => {
    const { path, ...init } = postJson('/api/admin-permissions', {
      user_id: userId,
      app_id: appId,
      role,
    });
    return call<{ role: Role | null }>(path, init);
  },

  listAnnouncements: () =>
    call<{ announcements: AdminAnnouncement[] }>('/api/admin-announcements'),

  createAnnouncement: (input: {
    title: string;
    body_md: string;
    valid_until: string | null;
    pinned: boolean;
  }) => {
    const { path, ...init } = postJson('/api/admin-announcements', input);
    return call<{ announcement: AdminAnnouncement }>(path, init);
  },

  updateAnnouncement: (
    input: {
      id: string;
      title: string;
      body_md: string;
      valid_until: string | null;
      pinned: boolean;
    },
  ) => {
    const { path, ...init } = postJson('/api/admin-announcements', input, 'PATCH');
    return call<{ announcement: AdminAnnouncement }>(path, init);
  },

  deleteAnnouncement: (id: string) =>
    call<{ id: string }>(`/api/admin-announcements?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  listPolls: () => call<{ polls: AdminPoll[] }>('/api/admin-polls'),

  createPoll: (input: PollInput) => {
    const { path, ...init } = postJson('/api/admin-polls', input);
    return call<{ poll: AdminPoll }>(path, init);
  },

  deletePoll: (id: string) =>
    call<{ id: string }>(`/api/admin-polls?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** Name;Antwort als CSV – nur fuer die anlegende Person, nie bei anonymen. */
  exportPoll: async (id: string): Promise<Blob> => {
    const res = await apiFetch(`/api/admin-polls?id=${encodeURIComponent(id)}&format=csv`);
    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(payload.error ?? `Fehler ${res.status}`);
    }
    return res.blob();
  },
};

/** Abfragen im Schwarzen Brett – fuer alle im Hub. */
export const pollsApi = {
  list: () => call<{ polls?: PollView[] }>('/api/polls').then((r) => r.polls ?? []),

  answer: (pollId: string, optionIds: string[]) => {
    const { path, ...init } = postJson('/api/polls', { poll_id: pollId, option_ids: optionIds });
    return call<{ ok: true }>(path, init);
  },
};
